import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import UserGroupDataService from "../services/UserGroupService";

import { debugLog, errorLog } from "../utils/debugUtils";
import { getRejectedError, toRejectedPayload } from "../utils/sliceErrorUtils";

export const initialState = {
	list: [],
	selectedGroup: null,
	status: {
		retrieve: "idle",
		retrieveOne: "idle",
		create: "idle",
		update: "idle",
		find: "idle",
	},
	error: {
		retrieve: null,
		retrieveOne: null,
		create: null,
		update: null,
		find: null,
	},
};

// UserGroup keeps the same structured rejection flow as the active User/Baseline
// slices so list/detail forms surface backend validation consistently.
export const createUserGroup = createAsyncThunk(
	"usergroups/create",
	async ({ data }, { rejectWithValue }) => {
		try {
			const res = await UserGroupDataService.create(data);
			debugLog(3, "[userGroupSlice][FLOW] createUserGroup response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[userGroupSlice][PROCESS] createUserGroup rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "User Group create failed."));
		}
	}
);

export const retrieveUserGroups = createAsyncThunk(
	"usergroups/retrieve",
	async (_, { rejectWithValue }) => {
		try {
			const res = await UserGroupDataService.getAll();
			debugLog(3, "[userGroupSlice][FLOW] retrieveUserGroups response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[userGroupSlice][PROCESS] retrieveUserGroups rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "User Group retrieve failed."));
		}
	}
);

export const retrieveOneUserGroup = createAsyncThunk(
	"usergroups/retrieveOne",
	async (id, { rejectWithValue }) => {
		try {
			const res = await UserGroupDataService.get(id);

			debugLog(3, "[userGroupSlice][FLOW] retrieveOneUserGroup response", { response: res.data });

			let tempRoles = [];
			let tempUsers = [];
			res?.data?.user_accesses.forEach(function (item) {
				if (item.user_role) tempRoles.push(item.user_role);
				if (item.user) tempUsers.push(item.user);
			});
			const transformed = { ...res.data, user_roles: tempRoles, users: tempUsers };

			debugLog(4, "[userGroupSlice][PROCESS] retrieveOneUserGroup transformed", {
				transformed: transformed,
			});

			return transformed;
		} catch (err) {
			errorLog(4, "[userGroupSlice][PROCESS] retrieveOneUserGroup rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "User Group retrieve failed."));
		}
	}
);

export const updateUserGroup = createAsyncThunk(
	"usergroups/update",
	async ({ id, data }, { rejectWithValue }) => {
		try {
			const res = await UserGroupDataService.update(id, data);
			debugLog(3, "[userGroupSlice][FLOW] updateUserGroup response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[userGroupSlice][PROCESS] updateUserGroup rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "User Group update failed."));
		}
	}
);

export const findUserGroupsByKeyword = createAsyncThunk(
	"usergroups/findByKeyword",
	async ({ keyword }, { rejectWithValue }) => {
		try {
			const res = await UserGroupDataService.findByKeyword(keyword);
			debugLog(3, "[userGroupSlice][FLOW] findUserGroupsByKeyword response", {
				response: res.data,
			});
			return res.data;
		} catch (err) {
			errorLog(4, "[userGroupSlice][PROCESS] findUserGroupsByKeyword rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "User Group search failed."));
		}
	}
);

const userGroupSlice = createSlice({
	name: "usergroups",
	initialState,
	reducers: {
		setSelectedGroup: (state, action) => {
			// safeguard against null/undefined/falsey payload
			state.selectedGroup = action.payload ?? null;
			debugLog(4, "[userGroupSlice][PROCESS] setSelectedGroup", { payload: action.payload });
		},
		clearSelectedGroup: (state) => {
			state.selectedGroup = null;
			debugLog(4, "[userGroupSlice][PROCESS] clearSelectedGroup", {});
		},
		resetError: (state, action) => {
			// Optional: reset all errors, or just one type based on payload
			if (action.payload) {
				// Reset only one error key, e.g. { type: "create" }
				state.error[action.payload.type] = null;
				debugLog(4, "[userGroupSlice][PROCESS] resetError one", { type: action.payload.type });
			} else {
				// Reset all error keys
				Object.keys(state.error).forEach((key) => {
					state.error[key] = null;
				});
				debugLog(4, "[userGroupSlice][PROCESS] resetError all", {});
			}
		},
	},
	extraReducers: (builder) => {
		builder

			// --- [retrieveUserGroups] ---
			.addCase(retrieveUserGroups.pending, (state) => {
				state.status.retrieve = "loading";
				state.error.retrieve = null;
			})
			.addCase(retrieveUserGroups.fulfilled, (state, action) => {
				state.status.retrieve = "succeeded";
				state.list = action.payload;
			})
			.addCase(retrieveUserGroups.rejected, (state, action) => {
				state.status.retrieve = "failed";
				state.error.retrieve = getRejectedError(action, "User Group retrieve failed.");
				debugLog(4, "[userGroupSlice][PROCESS] retrieveUserGroups rejected", { error: action.error });
			})

			// --- [retrieveOneUserGroup] ---
			.addCase(retrieveOneUserGroup.pending, (state) => {
				state.status.retrieveOne = "loading";
				state.error.retrieveOne = null;
			})
			.addCase(retrieveOneUserGroup.fulfilled, (state, action) => {
				state.status.retrieveOne = "succeeded";
				state.selectedGroup = action.payload;
			})
			.addCase(retrieveOneUserGroup.rejected, (state, action) => {
				state.status.retrieveOne = "failed";
				state.error.retrieveOne = getRejectedError(action, "User Group retrieve failed.", {
					expectNotFound: true,
				});
				debugLog(4, "[userGroupSlice][PROCESS] retrieveOneUserGroup rejected", {
					error: action.error,
				});
			})

			// --- [updateUserGroup] ---
			.addCase(updateUserGroup.pending, (state) => {
				state.status.update = "loading";
				state.error.update = null;
			})
			.addCase(updateUserGroup.fulfilled, (state, action) => {
				// Don't modify currentUserGroup if response is just a message
				// state.currentUserGroup = action.payload;
				state.status.update = "succeeded";
				state.error.update = null;

				// Optionally update the list
				const index = state.list.findIndex((u) => u.id === action.payload.id);
				if (index !== -1) state.list[index] = action.payload;

				debugLog(4, "[userGroupSlice][PROCESS] updateUserGroup fulfilled updated userGroup", {
					payload: action.payload,
				});
			})
			.addCase(updateUserGroup.rejected, (state, action) => {
				state.status.update = "failed";
				// Keep the richer error object so EditUserGroup can preserve backend
				// validation/business-rule messages instead of generic dialog text.
				state.error.update = getRejectedError(action, "User Group update failed.");
				debugLog(4, "[userGroupSlice][PROCESS] updateUserGroup rejected", { error: action.error });
			})

			// --- [createUserGroup] ---
			.addCase(createUserGroup.pending, (state) => {
				state.status.create = "loading";
				state.error.create = null;
			})
			.addCase(createUserGroup.fulfilled, (state, action) => {
				state.status.create = "succeeded";
				state.list.push(action.payload);
			})
			.addCase(createUserGroup.rejected, (state, action) => {
				state.status.create = "failed";
				state.error.create = getRejectedError(action, "User Group create failed.");
				debugLog(4, "[userGroupSlice][PROCESS] createUserGroup rejected", { error: action.error });
			})

			.addCase(findUserGroupsByKeyword.pending, (state) => {
				state.status.find = "loading";
				state.error.find = null;
			})
			.addCase(findUserGroupsByKeyword.fulfilled, (state, action) => {
				state.status.find = "succeeded";
				state.list = action.payload;
			})
			.addCase(findUserGroupsByKeyword.rejected, (state, action) => {
				state.status.find = "failed";
				// Keep find/search failures on the same normalized object contract as
				// the active retrieve/edit flows so search UI can reuse shared dialog
				// behavior without special-case string parsing later.
				state.error.find = getRejectedError(action, "User Group search failed.");
				debugLog(4, "[userGroupSlice][PROCESS] findUserGroupsByKeyword rejected", {
					error: action.error,
				});
			});
	},
});

const { reducer } = userGroupSlice;
export default reducer;

// export const { setCurrentUserGroup, clearCurrentUserGroup, resetError } = userGroupSlice.actions;
export const { setSelectedGroup, clearSelectedGroup, resetError } = userGroupSlice.actions;
